// Copyright (C) 2024 Nethesis S.r.l.
// SPDX-License-Identifier: AGPL-3.0-or-later

import React, { type FC, useMemo, useCallback, memo } from 'react'
import { useSelector } from 'react-redux'
import { RootState, Dispatch } from '../../store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPhone,
  faEarListen,
  faHandPointUp,
  faArrowLeft,
  faCircle,
  faUnlock,
  faUsers,
} from '@fortawesome/free-solid-svg-icons'
import { Button } from '../'
import Timer from './Timer'
import Number from './Number'
import DisplayName from './DisplayName'
import { AudioBars } from '../'
import { answerIncomingCall } from '../../lib/phone/call'
import Avatar from './Avatar'
import Actions from './Actions'
import Hangup from '../Hangup'
import { useTranslation } from 'react-i18next'
import { faOfficePhone } from '@nethesis/nethesis-solid-svg-icons'
import { isPhysical } from '../../lib/user/default_device'
import { CustomThemedTooltip } from '../CustomThemedTooltip'
import StreamingImage from '../StreamingImage'
import VideoStreamingSkeleton from '../VideoStreamingSkeleton'
import VideoStreamingEmptyState from '../VideoStreamingEmptyState'
import { handleStreamingUnlock } from '../../utils'
import { useDispatch } from 'react-redux'

function isAnswerVisible(outgoing: boolean, accepted: boolean): boolean {
  return !outgoing && !accepted
}

const Details = memo(
  ({
    children,
    className = 'pi-gap-1 pi-grid-rows-2',
  }: {
    children: React.ReactNode
    className?: string
  }) => (
    <div className={`pi-grid pi-min-w-0 pi-self-center pi-grid-cols-1 ${className}`}>
      {children}
    </div>
  ),
)
Details.displayName = 'Details'

const QueueBadge = memo(
  ({
    label,
    tooltipContent,
  }: {
    label: string
    tooltipContent?: string
  }) => {
    if (!label) {
      return null
    }

    return (
      <span
        className='pi-inline-flex pi-flex-none pi-items-center pi-justify-center pi-text-iconSecondary'
        data-tooltip-id={tooltipContent ? 'tooltip-queue' : undefined}
        data-tooltip-content={tooltipContent || ''}
      >
        <FontAwesomeIcon icon={faUsers} className='pi-h-4 pi-w-4' />
      </span>
    )
  },
)
QueueBadge.displayName = 'QueueBadge'

const IncomingQueueBadge = memo(
  ({
    label,
    tooltipContent,
  }: {
    label: string
    tooltipContent?: string
  }) => {
    if (!label) {
      return null
    }

    return (
      <span
        className='pi-inline-flex pi-w-fit pi-max-w-full pi-flex-none pi-items-center pi-gap-1.5 pi-self-start pi-rounded-full pi-bg-iconSecondary pi-px-2.5 pi-py-1 pi-text-xs pi-font-medium pi-leading-none pi-text-iconWhite dark:pi-bg-iconSecondaryDark'
        data-tooltip-id={tooltipContent ? 'tooltip-queue' : undefined}
        data-tooltip-content={tooltipContent || ''}
        aria-label={tooltipContent || label}
      >
        <FontAwesomeIcon icon={faUsers} className='pi-h-3 pi-w-3 pi-flex-none' />
        <span className='pi-block pi-max-w-28 pi-truncate'>{label}</span>
      </span>
    )
  },
)
IncomingQueueBadge.displayName = 'IncomingQueueBadge'

/**
 * The main view to manage calls, the starting point for calls actions flows
 */
const CallView: FC<CallViewProps> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()

  const currentCall = useSelector((state: RootState) => state.currentCall)
  const {
    incoming,
    accepted,
    outgoing,
    startTime,
    paused,
    number,
    isRecording,
    username,
    streamingSourceNumber,
    queueName,
    queueNumber,
    throughQueue,
  } = currentCall

  const { isOpen, isFromStreaming } = useSelector((state: RootState) => state.island)
  const { remoteAudioStream } = useSelector((state: RootState) => state.webrtc)
  const listenState = useSelector((state: RootState) => state.listen)
  const { isListen, isIntrude, isListenExtension, isIntrudeExtension } = listenState
  const { data: alertsData } = useSelector((state: RootState) => state.alerts)
  const currentUser = useSelector((state: RootState) => state.currentUser)
  const { videoSources, sourceImages } = useSelector((state: RootState) => state.streaming)

  const activeAlerts = useMemo(
    () => Object.values(alertsData).filter((alert: any) => alert.active),
    [alertsData],
  )

  const latestAlert = useMemo(
    () => (activeAlerts.length > 0 ? activeAlerts[activeAlerts.length - 1] : null),
    [activeAlerts],
  )

  const shouldShowStreamingImage = useMemo(
    () => isFromStreaming && streamingSourceNumber && !accepted,
    [isFromStreaming, streamingSourceNumber, accepted],
  )

  // Check if unlock button should be shown and get tooltip text
  const unlockData = useMemo(() => {
    if (!streamingSourceNumber || !videoSources) return { canUnlock: false, tooltipText: '' }

    const source = Object.values(videoSources).find(
      (source) => source?.extension === streamingSourceNumber,
    )

    const canUnlock = Boolean(source?.cmdOpen && source?.cmdOpen.trim() !== '')
    const tooltipText =
      canUnlock && source ? `${t('VideoStreaming.Open')}: ${source?.description}` : ''

    return { canUnlock, tooltipText }
  }, [streamingSourceNumber, videoSources, t])

  const hasValidUsername = useMemo(() => username !== '' && username !== 'undefined', [username])

  const queueLabel = useMemo(() => queueName || queueNumber || '', [queueName, queueNumber])

  const queueTooltipContent = useMemo(() => {
    if (!throughQueue || !queueLabel) {
      return ''
    }

    return `${t('Common.Queue')}: ${queueLabel}`
  }, [throughQueue, queueLabel, t])

  const shouldShowIncomingQueueBadge = useMemo(
    () => Boolean(incoming && !accepted && throughQueue && queueLabel),
    [accepted, incoming, throughQueue, queueLabel],
  )

  const renderLandlinePhoneDiv = useCallback(
    () => (
      <div className='pi-text-gray-600 dark:pi-text-gray-300 pi-font-normal pi-text-sm pi-flex pi-items-center pi-truncate'>
        <FontAwesomeIcon size='sm' icon={faOfficePhone} className='pi-mr-1' />
        <span className='pi-max-w-16 pi-truncate'>
          {currentUser?.default_device?.description || t('Common.Physical phone')}
        </span>
      </div>
    ),
    [currentUser?.default_device?.description, t],
  )

  const renderPulseIcon = useCallback(
    (color: string) => {
      const sizeClasses = !isOpen ? 'pi-h-6 pi-w-6' : 'pi-h-12 pi-w-12'
      const innerSizeClasses = !isOpen ? 'pi-h-4 pi-w-4 pi-rounded-full' : 'pi-h-8'
      const animationSizeClasses = !isOpen ? 'pi-h-6 pi-w-6' : 'pi-w-8 pi-h-8'
      const colorClasses = color === 'red' ? 'pi-bg-red-400' : 'pi-bg-green-400'
      const textColorClasses = color === 'red' ? 'pi-text-red-500' : 'pi-text-green-500'

      return (
        <div className={`${sizeClasses} pi-flex pi-justify-center pi-items-center`}>
          <div
            className={`${innerSizeClasses} pi-w-fit pi-flex pi-justify-center pi-items-center pi-gap-1 pi-overflow-hidden`}
          >
            <span
              className={`${animationSizeClasses} pi-animate-ping pi-absolute pi-inline-flex pi-rounded-full ${colorClasses} pi-opacity-75`}
            ></span>
            <FontAwesomeIcon
              className={`pi-w-4 pi-h-6 pi-rotate-45 ${textColorClasses}`}
              icon={faCircle}
            />
          </div>
        </div>
      )
    },
    [isOpen],
  )

  const callViewClasses = useMemo(() => {
    let baseClasses = 'pi-grid pi-items-center'

    if (isOpen) {
      baseClasses = shouldShowIncomingQueueBadge ? 'pi-grid pi-items-center' : 'pi-grid pi-items-start'

      if (accepted) {
        baseClasses += ' pi-grid-rows-[72px_1fr]'
      } else if (incoming) {
        baseClasses += ' pi-grid-cols-[256px_114px]'
      } else if (outgoing) {
        baseClasses += ' pi-grid-cols-[1fr_84px]'
      }
    }

    return shouldShowStreamingImage ? 'pi-flex pi-flex-col' : baseClasses
  }, [isOpen, accepted, incoming, outgoing, shouldShowStreamingImage, shouldShowIncomingQueueBadge])

  const topContentClasses = useMemo(() => {
    let columns = ''

    if (isOpen && !accepted && outgoing) {
      columns = 'pi-grid-cols-[48px_1fr]'
    } else if (isOpen && !accepted && incoming) {
      columns = 'pi-grid-cols-[48px_1fr_1px]'
    } else if (isOpen && accepted) {
      columns = 'pi-grid-cols-[48px_164px_48px]'
    } else if (!isOpen && !accepted) {
      columns = 'pi-grid-cols-[24px_102px]'
    } else {
      columns = 'pi-grid-cols-[24px_66px_24px]'
    }

    const itemsAlign = isOpen && !shouldShowIncomingQueueBadge ? 'start' : 'center'

    return `pi-grid ${columns} pi-gap-${isOpen ? '5' : '3'} pi-items-${itemsAlign} pi-justify-center pi-w-full`
  }, [isOpen, accepted, incoming, outgoing, shouldShowIncomingQueueBadge])

  const getGridClasses = useMemo(() => {
    if (shouldShowStreamingImage) return 'pi-grid-cols-3'
    if (isAnswerVisible(outgoing, accepted)) return 'pi-grid-cols-2'
    if (accepted) return 'pi-grid-cols-1 pi-justify-items-center'
    return 'pi-grid-cols-1 pi-justify-items-end'
  }, [shouldShowStreamingImage, outgoing, accepted])

  const renderStatusIcon = useCallback(() => {
    const iconSizeClass = isOpen
      ? 'pi-relative pi-z-30 pi-h-12 pi-w-12 pi-rounded-sm pi-bg-cover'
      : 'pi-relative pi-z-30 pi-h-6 pi-w-6 pi-rounded-sm pi-bg-cover'

    if (isListen) {
      return <FontAwesomeIcon className={iconSizeClass} icon={faEarListen} />
    }

    if (isIntrude) {
      return <FontAwesomeIcon className={iconSizeClass} icon={faHandPointUp} />
    }

    if (number !== '' && hasValidUsername) {
      return <Avatar />
    }

    if (incoming && !hasValidUsername) {
      return <FontAwesomeIcon className={`${iconSizeClass} pi--rotate-45`} icon={faArrowLeft} />
    }

    if (accepted && !outgoing && !hasValidUsername) {
      return <FontAwesomeIcon className={`${iconSizeClass} pi--rotate-45`} icon={faArrowLeft} />
    }

    if (outgoing && !hasValidUsername) {
      return (
        <FontAwesomeIcon className={`${iconSizeClass} pi-rotate-[135deg]`} icon={faArrowLeft} />
      )
    }

    return null
  }, [isOpen, isListen, isIntrude, number, hasValidUsername, incoming, outgoing, accepted])

  const renderDetails = useCallback(() => {
    if (!isOpen) return null

    if (isIntrude) {
      return (
        <Details>
          <span className='pi-justify-center pi-w-fit pi-relative pi-inline-block pi-font-bold pi-text-base'>
            {t('Common.Intrude')}
            {isIntrudeExtension ? ` - ${isIntrudeExtension}` : ''}
          </span>
          {accepted ? (
            !isPhysical() ? (
              <Timer startTime={startTime} isNotAlwaysWhite />
            ) : (
              renderLandlinePhoneDiv()
            )
          ) : isIntrudeExtension ? (
            `${isIntrudeExtension}`
          ) : (
            ''
          )}
        </Details>
      )
    }

    if (isListen) {
      return (
        <Details>
          <span className='pi-justify-center pi-w-fit pi-relative pi-inline-block pi-font-bold pi-text-base'>
            {t('Common.Listen')}
            {isListenExtension ? ` - ${isListenExtension}` : ''}
          </span>
          {accepted ? (
            !isPhysical() ? (
              <Timer startTime={startTime} isNotAlwaysWhite />
            ) : (
              renderLandlinePhoneDiv()
            )
          ) : isListenExtension ? (
            `${isListenExtension}`
          ) : (
            ''
          )}
        </Details>
      )
    }

    if (shouldShowIncomingQueueBadge) {
      return (
        <Details className='pi-grid-rows-[20px_24px_24px] pi-gap-0.5'>
          <IncomingQueueBadge label={queueLabel} tooltipContent={queueTooltipContent} />
          <DisplayName />
          <Number />
        </Details>
      )
    }

    return (
      <Details>
        {accepted && throughQueue && queueLabel ? (
          <div className='pi-flex pi-min-w-0 pi-items-center pi-gap-2'>
            <div className='pi-min-w-0 pi-flex-1'>
              <DisplayName />
            </div>
            <QueueBadge label={queueLabel} tooltipContent={queueTooltipContent} />
          </div>
        ) : (
          <DisplayName />
        )}
        {accepted ? (
          !isPhysical() ? (
            <Timer startTime={startTime} isNotAlwaysWhite />
          ) : (
            renderLandlinePhoneDiv()
          )
        ) : (
          <Number />
        )}
      </Details>
    )
  }, [
    isOpen,
    isIntrude,
    isListen,
    accepted,
    startTime,
    isIntrudeExtension,
    isListenExtension,
    t,
    renderLandlinePhoneDiv,
    accepted,
    throughQueue,
    queueLabel,
    queueTooltipContent,
    shouldShowIncomingQueueBadge,
  ])

  const renderAudioIndicator = useCallback(() => {
    if (accepted && isRecording) {
      return renderPulseIcon('red')
    }

    if (accepted && remoteAudioStream && !isPhysical()) {
      return (
        <AudioBars
          audioStream={remoteAudioStream}
          paused={paused}
          size={isOpen ? 'large' : 'small'}
        />
      )
    }

    if (accepted && isPhysical()) {
      return renderPulseIcon('green')
    }

    return null
  }, [accepted, isRecording, remoteAudioStream, isOpen, paused, renderPulseIcon])

  const renderStreamingContent = useCallback(() => {
    // Show skeleton while videoSources are loading or if streaming source number is not set yet
    if (!videoSources || Object.keys(videoSources).length === 0 || !streamingSourceNumber) {
      return <VideoStreamingSkeleton className='pi-w-full pi-h-40 pi-mt-4' />
    }

    // Find the streaming source
    const source = Object.values(videoSources).find(
      (source) => source.extension === streamingSourceNumber,
    )

    // If source doesn't exist, show empty state
    if (!source) {
      return <VideoStreamingEmptyState className='pi-w-full pi-h-40 pi-mt-4' />
    }

    // Check if image is available
    const hasImage = sourceImages[source.id] || source.image

    // If no image available, show empty state
    if (!hasImage) {
      return <VideoStreamingEmptyState className='pi-w-full pi-h-40 pi-mt-4' />
    }

    // If we have an image, show StreamingImage component
    return <StreamingImage />
  }, [streamingSourceNumber, videoSources, sourceImages])

  // Early return AFTER all hooks have been called
  if (latestAlert !== null) {
    return null
  }

  const setVideoStreamingAnswer = () => {
    answerIncomingCall()
    // Set view as video streaming answer with a small delay
    setTimeout(() => {
      dispatch.island.setIslandView('streamingAnswer')
    }, 200)
  }

  return (
    <div className='pi-bg-red pi-content-center pi-justify-center'>
      <div className={callViewClasses}>
        {shouldShowStreamingImage ? (
          <>
            <div className='pi-flex pi-items-center pi-justify-between pi-w-full pi-gap-2'>
              <div className='pi-flex pi-items-center pi-gap-4 pi-min-w-0 pi-flex-1'>
                {renderStatusIcon()}
                <div className='pi-flex pi-flex-col pi-justify-center pi-space-y-2 pi-min-w-0'>
                  <DisplayName />
                  <Number />
                </div>
              </div>

              <div className='pi-flex pi-gap-2 pi-flex-shrink-0'>
                <Hangup description={t('Tooltip.Hangup')} />
                {!outgoing && (
                  <Button
                    onClick={() => setVideoStreamingAnswer()}
                    variant='green'
                    data-tooltip-id='tooltip-answer'
                    data-tooltip-content={t('Tooltip.Answer') || ''}
                  >
                    <FontAwesomeIcon className='pi-w-5 pi-h-5' icon={faPhone} />
                  </Button>
                )}
                {/* Open door button - only show if cmdOpen is valid */}
                {unlockData?.canUnlock && (
                  <Button
                    variant='default'
                    onClick={handleStreamingUnlock}
                    data-tooltip-id='tooltip-unlock'
                    data-tooltip-content={unlockData?.tooltipText}
                  >
                    <FontAwesomeIcon className='pi-w-5 pi-h-5' icon={faUnlock} />
                  </Button>
                )}
              </div>
            </div>

            {renderStreamingContent()}
          </>
        ) : (
          <>
            <div className={topContentClasses}>
              {renderStatusIcon()}
              {renderDetails()}

              {!isOpen && !accepted && <DisplayName />}
              {!isOpen && accepted && <Timer startTime={startTime} isNotAlwaysWhite />}

              {renderAudioIndicator()}
            </div>

            {isOpen && (
              <>
                <div className={`${!(isListen || isIntrude) ? 'pi-grid pi-gap-y-5' : ''}`}>
                  {accepted && <Actions />}
                  <div className={`pi-grid ${getGridClasses} pi-gap-3.5`}>
                    <Hangup description={t('Tooltip.Hangup and transfer')} />

                    {isAnswerVisible(outgoing, accepted) && (
                      <Button
                        onClick={answerIncomingCall}
                        variant='green'
                        data-tooltip-id='tooltip-answer-left'
                        data-tooltip-content={t('Tooltip.Answer') || ''}
                      >
                        <FontAwesomeIcon className='pi-w-6 pi-h-6' icon={faPhone} />
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <CustomThemedTooltip id='tooltip-answer-left' place='left' />
      <CustomThemedTooltip id='tooltip-answer' place='left' />
      <CustomThemedTooltip id='tooltip-unlock' place='left' />
      <CustomThemedTooltip id='tooltip-display-name' place='bottom' />
      <CustomThemedTooltip id='tooltip-queue' place='bottom' />
    </div>
  )
}

export default memo(CallView)

export interface CallViewProps {}
